import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { History, ChevronRight, Loader2 } from "lucide-react";

import api from "../../services/api";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// O que a pessoa já tratou com a empresa, ao lado da conversa aberta.
//
// O histórico de um contato fica picado em vários atendimentos, e da conversa
// não havia caminho nenhum até os anteriores: para saber o que já tinha sido
// resolvido era preciso caçar na busca global e abrir um a um. A página de
// linha do tempo do contato existia, mas nenhuma tela levava até ela.

const ROTULO_DO_STATUS = {
  open: "Em atendimento",
  pending: "Aguardando",
  closed: "Encerrado",
};

const CLASSE_DO_STATUS = {
  open: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  closed: "bg-muted text-muted-foreground",
};

// Quantas linhas antes de "ver todos". Cinco cobre o caso comum sem transformar
// o painel numa segunda lista de conversas.
const VISIVEIS = 5;

const AtendimentosAnteriores = ({ contactId, ticketId }) => {
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    if (!contactId) {
      setLista(null);
      return;
    }

    let vivo = true;

    setCarregando(true);
    setErro(false);
    setExpandido(false);

    api
      .get(`/contacts/${contactId}/tickets`, {
        params: ticketId ? { exceptTicketId: ticketId } : undefined,
      })
      .then(({ data }) => {
        if (!vivo) return;
        setLista(Array.isArray(data) ? data : []);
      })
      .catch(() => vivo && setErro(true))
      .finally(() => vivo && setCarregando(false));

    return () => {
      vivo = false;
    };
  }, [contactId, ticketId]);

  if (!contactId) return null;

  if (carregando) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Buscando atendimentos anteriores…
      </div>
    );
  }

  // Falhar a consulta não pode esconder o resto do painel do contato, mas
  // também não pode fingir que a pessoa nunca conversou antes: some com o
  // bloco só quando a resposta veio e veio vazia.
  if (erro) {
    return (
      <div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
        Não foi possível carregar os atendimentos anteriores.
      </div>
    );
  }

  if (!lista?.length) {
    return (
      <div className="rounded-xl border bg-card p-4">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <History className="h-4 w-4 text-muted-foreground" />
          Atendimentos anteriores
        </p>
        <p className="pt-1 text-xs text-muted-foreground">
          Esta é a primeira conversa deste contato.
        </p>
      </div>
    );
  }

  const mostrados = expandido ? lista : lista.slice(0, VISIVEIS);

  return (
    <div className="space-y-2 rounded-xl border bg-card p-4">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <History className="h-4 w-4 text-muted-foreground" />
        Atendimentos anteriores
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {lista.length}
        </span>
      </p>

      <div className="-mx-1 space-y-0.5">
        {mostrados.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(`/tickets/${item.id}`)}
            className="flex w-full items-start gap-2 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-accent"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium tabular-nums">
                  #{item.id}
                </span>
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    CLASSE_DO_STATUS[item.status] || CLASSE_DO_STATUS.closed,
                  )}
                >
                  {ROTULO_DO_STATUS[item.status] || item.status}
                </span>
                <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                  {format(parseISO(item.updatedAt), "dd/MM/yy")}
                </span>
              </div>

              {/* O assunto: sem ele a lista é uma coluna de datas e ninguém
                  sabe qual abrir. */}
              {item.ultimaMensagem && (
                <p className="truncate pt-0.5 text-xs text-muted-foreground">
                  {item.ultimaMensagem}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-1 text-[11px] text-muted-foreground">
                {item.queue && (
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: item.queue.color }}
                    />
                    {item.queue.name}
                  </span>
                )}
                {item.user && <span>{item.user.name}</span>}
                {item.totalMensagens > 0 && (
                  <span>{item.totalMensagens} msg</span>
                )}
                {/* Encerrado por quê. "Resolvido" e "cliente sumiu" ficam
                    iguais na lista e são coisas opostas. */}
                {item.closeReason && (
                  <span className="italic">{item.closeReason}</span>
                )}
              </div>
            </div>

            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>

      {lista.length > VISIVEIS && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full text-xs"
          onClick={() => setExpandido((v) => !v)}
        >
          {expandido
            ? "Mostrar menos"
            : `Ver os outros ${lista.length - VISIVEIS}`}
        </Button>
      )}

      {/* A linha do tempo completa: mensagens, mídias e mudanças de etapa do
          contato inteiro, e não só a lista de atendimentos. A página existia
          e não havia como chegar nela por nenhuma tela. */}
      <Button
        variant="outline"
        size="sm"
        className="h-7 w-full text-xs"
        onClick={() => navigate(`/contacts/${contactId}/timeline`)}
      >
        Ver linha do tempo completa
      </Button>
    </div>
  );
};

export default AtendimentosAnteriores;
